import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { Params, Router } from '@angular/router';
import cloneDeep from 'lodash/cloneDeep';

import { Vragenboom, Vraagtype, Controltype, Vraag, Antwoord } from '@models/claims';
import {
  ClaimVoorbereidenEventargs, VraagboomAntwoord,
  IBaseVraagboomResultaat, Groep, SchademeldenState, Verzekering, Meetgegevens, VraagboomEmptyResultaat,
  VraagboomUploadResultaat, CurrentvraagEventargs, VraagboomDateResultaat
} from '@models/vragenboom';
import { Proces } from '../../models/imports/generic';
import { ResultaatMapper } from '../../mappers/resultaat.mapper';
import { HiddenVraagMapper } from '../../mappers/hidden-vraag.mapper';
import { DataService } from '../../services/data.service';
import { MetenMapper } from '../../mappers/meten.mapper';
import { FormatHelper, VraagHelper } from '@services/public_api';
import { RefreshSessionService } from '../../services/refresh-session.service';

@Component({
  selector: 'app-vragenboom',
  templateUrl: './vragenboom.component.html',
  styleUrls: ['./vragenboom.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VragenboomComponent implements OnInit {
  private controltype = Controltype;
  private schademeldenState = SchademeldenState;
  private vraagtype = Vraagtype;
  private _params: Params;
  private _vragenboom: Vragenboom;
  get vragenboom(): Vragenboom {
    return this._vragenboom;
  }

  private _vraagIdsWithExternalConditions: Set<string>;
  private get vraagIdsWithExternalConditions(): Set<string> {
    if (!this._vragenboom)
      return new Set<string>();

    if (this._vraagIdsWithExternalConditions)
      return this._vraagIdsWithExternalConditions;

    this._vraagIdsWithExternalConditions = new Set<string>();
    this.vragenboom.vragen?.forEach(vraag =>
      vraag?.conditions?.forEach(condition =>
        condition?.antwoorden?.forEach(antwoord => {
          if (antwoord != null && antwoord.vraagId != vraag.id)
            this._vraagIdsWithExternalConditions.add(antwoord.vraagId);
        })));

    return this._vraagIdsWithExternalConditions;
  }

  @ViewChild('currentVraag') currentVraagElement: ElementRef;

  @Input()
  set vragenboom(value: Vragenboom) {
    this._vragenboom = value;
    this.initializeVragenboom();
  }
  @Input() status: SchademeldenState;
  @Input() header: string;
  @Input() headerIcon: string;
  get headerIconUrl(): string {
    const icon = '/assets/icons/ols/' + this.headerIcon;
    return `url(${icon})`;
  }
  @Input() set params(value: Params) {
    this._params = value;
    if (value) {
      this.prefillAnswers();
    }
  }
  @Output() prepareClaimVoorbereiden: EventEmitter<ClaimVoorbereidenEventargs> = new EventEmitter<ClaimVoorbereidenEventargs>();
  @Output() currentVraagChange: EventEmitter<CurrentvraagEventargs> = new EventEmitter<CurrentvraagEventargs>();
  @Output() submitVraagboom: EventEmitter<VraagboomAntwoord<IBaseVraagboomResultaat>[]>
    = new EventEmitter<VraagboomAntwoord<IBaseVraagboomResultaat>[]>();
  @Input() meetKey: string;

  currentVraag: Vraag;
  alleResultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[] = [];
  vragenReeks: Vraag[] = [];
  get visibleVragenReeks(): Vraag[] {
    if (this.vragenReeks) {
      return this.vragenReeks.filter(v => v.tonen);
    }
    return null;
  }
  vraagGroepen: Groep[] = [];
  isWijzigen = false;
  _meetgegevens: Meetgegevens;
  _verzekering: Verzekering;
  forceParseUitgevoerd = false;
  verzekeringOpgehaald = false;
  proces: Proces;
  nextVraagIdBewaren: string;
  idSchadecoach: string;
  idsSchadecoach: string;
  schadecoachKlaar: boolean;

  originalFirstQuestionId: string;
  originalVragenReeks: Vraag[];
  originalAlleResultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[];

  get bepaalHasTerug(): boolean {
    if (this.hasPreviousQuestion(this.currentVraag) || this.isEersteVraagKiezer(this.currentVraag)) {
      return true;
    }
    return false;
  }

  constructor(
    private _dataService: DataService,
    private _router: Router,
    private _refreshSessionService: RefreshSessionService
  ) { }

  ngOnInit() {
    this._meetgegevens = this._dataService.getMeetgegevens();
    if (!this.verzekeringOpgehaald) {
      this._verzekering = this._dataService.getVerzekering();
      this.verzekeringOpgehaald = true;
    }
    this.proces = this._dataService.getProces();
  }

  initializeVragenboom() {
    this.clearCurrentState();
    const startVraag = this.vragenboom.vragen.find(vraag => vraag.startvraag === true);
    this.addVraagToReeks(startVraag);
    this.setCurrentVraag(startVraag);
  }

  prefillAnswers() {
    let prefillen = true;

    while (prefillen) {
      let currentVraagId = this.currentVraag.id;
      prefillen = false;
      if (this._params[this.currentVraag.id]) {
        let antwoordId = this._params[this.currentVraag.id];
        let antw = this.currentVraag.antwoorden?.find(a => a.id === antwoordId);

        // check of antwoord niet vaker voorkomt, bijvoorbeeld meerdere auto's
        if (antw && this.currentVraag.antwoorden.filter(a => a.titel === antw.titel).length === 1) {
          let antwoordResultaat = new VraagboomAntwoord<VraagboomEmptyResultaat>();
          antwoordResultaat.vraag = this.currentVraag;
          antwoordResultaat.antwoorden = [].concat(antw);
          antwoordResultaat.resultaat = new VraagboomEmptyResultaat();
          antwoordResultaat.isValid = true;

          this.onAntwoordResultaatChange(this.currentVraag, antwoordResultaat);

          // Check if currentVraag is not an eindvraag and check if 'new' current vraag is changed
          if (!this.currentVraag.eindvraag && currentVraagId !== this.currentVraag.id) {
            prefillen = true;
          }
        }
      }
    }

    setTimeout(() => {
      this.scrollElementIntoView(this.currentVraagElement.nativeElement);
    }, 2000);
  }

  clearCurrentState(): void {
    this.vragenReeks = [];
    this.alleResultaten = [];
    this.vraagGroepen = [];
    this.currentVraag = null;
    this.isWijzigen = false;
  }

  headerIconSvg(): boolean {
    return this.headerIcon?.indexOf('.svg') > -1;
  }

  addVraagToReeks(vraag: Vraag): void {
    if (vraag != null && this.vragenReeks != null) {
      if (this.vragenReeks.findIndex(v => v.id === vraag.id) < 0) {
        const firstIndexOfGroep: number = this.vragenReeks.findIndex(v => v.groep_id === vraag.groep_id);

        if (firstIndexOfGroep < 0) {
          this.vragenReeks.push(cloneDeep(vraag));
        } else {
          const vragenOfGroep: Vraag[] = this.vragenReeks.filter(v => v.groep_id === vraag.groep_id);
          if ((firstIndexOfGroep + vragenOfGroep.length) > (this.vragenReeks.length - 1)) {
            this.vragenReeks.push(cloneDeep(vraag));
          } else {
            this.vragenReeks.splice(firstIndexOfGroep + vragenOfGroep.length, 0, vraag);
          }
        }
      }

      if (vraag.tonen) {
        this.updateGroepenOfCurrentReeks(vraag);
      }
    }
  }

  removeUnusedQuestionsFromGroup(vraag: Vraag) {
    // Iedere mag maximaal bestaan uit 1 groep (uitzondering voor single/multi choice vragen)
    // STP bevat echter meerdere 'dezelfde vragen' voor bijvoorbeeld beschreven van oorzaak
    // Oplossing: Verwijder eventueel overige vragen uit groep en correleer eerder gegeven antwoorden (mits mogelijk).
    if (vraag != null && vraag.tonen) {

      if (this.vragenReeks == null || this.alleResultaten == null) {
        return [];
      }
      const newReeks: Vraag[] = [];
      let currentvraag: Vraag = this.vragenReeks.find(v => v.startvraag);
      while (currentvraag != null && !VraagHelper.isEindvraag(currentvraag)) {
        newReeks.push(currentvraag);
        const resultaat = this.alleResultaten.find(r => r.vraag.id === currentvraag.id);
        if (resultaat == null) {
          // TODO fout situaties
          break;
        }
        currentvraag = this.vragenReeks.find(v => v.id === resultaat.calculateNextVraagId(this.alleResultaten));
      }

      if (currentvraag != null && newReeks.findIndex(v => v.id === currentvraag.id) < 0) {
        newReeks.push(currentvraag);
      }
      this.vragenReeks = newReeks;
    }
  }

  antwoordOpslaan(resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>) {
    if (!resultaat) {
      return;
    }

    const index = this.alleResultaten.findIndex(a => a.vraag.id === resultaat.vraag.id);
    if (index === -1) {
      this.alleResultaten.push(cloneDeep(resultaat));
    } else {
      this.alleResultaten[index] = cloneDeep(resultaat);
    }
    this._dataService.saveResultaten(this.alleResultaten);
  }

  wijzigenVraag(resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>): void {
    if (resultaat != null) {
      if (this.currentVraag != null) {
        const currentgroep: Groep = this.vraagGroepen.find(g => g.id === this.currentVraag.groep_id);
        const resultaatCurrentVraag = currentgroep.resultaten.find(r => r.vraag.id === this.currentVraag.id);
        if (resultaatCurrentVraag && resultaatCurrentVraag.isValid === true) {
          this.antwoordOpslaan(resultaatCurrentVraag);
        }
      }

      // Update session timeout to prevent server side session expiration
      this._refreshSessionService.updateSessionTimeout();

      this.setCurrentVraag(resultaat.vraag);

      this.isWijzigen = true;
      this.originalVragenReeks = cloneDeep(this.vragenReeks);
      this.originalAlleResultaten = cloneDeep(this.alleResultaten);
      this.originalFirstQuestionId = this.currentVraag.id;
    }
  }

  isSchadeafhandeling(): Boolean {
    if (this.status === SchademeldenState.Schademelden) {
      return true;
    }
    return false;
  }

  vorigeVraag(vraag: Vraag, resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>) {
    if (resultaat != null && resultaat.isValid) {
      // Er is een keuze gemaakt dus antwoord opslaan en vorige vraag opzoeken
      this.antwoordOpslaan(resultaat);

      const newNextQuestionId: string = resultaat.calculateNextVraagId(this.alleResultaten);
      const oldResultaat = this.alleResultaten.find(a => a.vraag.id === resultaat.vraag.id);
      if (oldResultaat == null || oldResultaat.calculateNextVraagId(this.alleResultaten) !== newNextQuestionId ||
        this.vragenReeks.findIndex(v => v.id === newNextQuestionId) < 0) {
        // Change in vragenReeks
        const currentIndex = this.vragenReeks.findIndex(v => v.id === vraag.id);
        if (currentIndex !== this.vragenReeks.length - 1) {
          // huidige vraag is niet de laatste in de reeks
          const vragenOfCurrentGroep: Vraag[] = this.vragenReeks.filter(v => v.groep_id === vraag.groep_id);
          const numberToDelete = vragenOfCurrentGroep.length - (vragenOfCurrentGroep.findIndex(v => v.id === vraag.id));
          if (numberToDelete > 1) {
            this.vragenReeks.splice(currentIndex + 1, numberToDelete - 1);
          }
        }
      }
    }

    if (resultaat != null && !resultaat.isValid) {
      // Kan voorkomen dat het resultaat al opgeslagen is, dan resultaat wissen
      const index = this.alleResultaten.findIndex(a => a.vraag.id === resultaat.vraag.id);
      if (index > -1) {
        this.alleResultaten = this.alleResultaten.filter(r => r.vraag.id !== resultaat.vraag.id);
      }
    }

    if (vraag.startvraag || this.visibleVragenReeks.length <= 1) {
      if (this.proces === Proces.Schademelden || this.proces === Proces.SchademeldenZakelijk) {
        this._router.navigate(['/kiezer']);
      }
      if (this.proces === Proces.SchademeldenZakelijkAnoniem) {
        this._router.navigate(['/intro'], { queryParams: { sl: this._verzekering.sleutel } });
      }
    } else {
      const currentIndex = this.visibleVragenReeks.findIndex(v => v.id === vraag.id);
      const prevVraag = this.visibleVragenReeks[currentIndex - 1];
      this.setCurrentVraag(prevVraag);
    }
  }

  annulerenWijzigen() {
    this.vragenReeks = cloneDeep(this.originalVragenReeks);
    this.alleResultaten = cloneDeep(this.originalAlleResultaten);
    this.originalVragenReeks = null;
    this.originalAlleResultaten = null;

    const lastQuestion: Vraag = this.vragenReeks[this.vragenReeks.length - 1];
    this.setCurrentVraag(lastQuestion);
    this.updateGroepenOfCurrentReeks(lastQuestion);
    this.isWijzigen = false;
  }

  onAntwoordResultaatChange(vraag: Vraag, resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>) {
    if ([Controltype.MultiChoice, Controltype.SingleChoice, Controltype.SingleChoiceComingDates, Controltype.SingleChoiceRepair, Controltype.SingleChoiceRepairWithSearch, Controltype.SingleChoiceWithDescription, Controltype.SingleChoiceWithSearch, Controltype.Telefoon1, Controltype.Telefoon2, Controltype.Telefoon3, Controltype.Referentie].includes(vraag?.control)) {
      // Determine if answers is not last of groep --> click on 'Save' button not required
      const nextVraagId: string = resultaat.calculateNextVraagId(this.alleResultaten);
      const currentResultaat = this.alleResultaten.find(r => r.vraag.id === vraag.id);
      const currentResultaatNextVraagId = currentResultaat?.calculateNextVraagId(this.alleResultaten);
      this.nextVraagIdBewaren = nextVraagId;

      // if result is new or next vraag is different then previously saved result
      if (currentResultaat == null || nextVraagId !== currentResultaatNextVraagId) {
        const nextVraag: Vraag = this.nextVisibleQuestion(resultaat);
        if (nextVraag.groep_id === this.currentVraag.groep_id) {
          if (this.status === SchademeldenState.Verzekeringkiezer && (vraag.control === Controltype.SingleChoice || vraag.control === Controltype.SingleChoiceWithSearch)) {
            this.alleResultaten = this.alleResultaten.filter(r => r.vraag.id !== nextVraag.id);
            this.opslaanVraag(vraag, resultaat);
          } else if (nextVraagId !== currentResultaatNextVraagId) {
            // keten wijzigt dus vragenreeks en resultaat aanpassen
            const indexVervallenVraag = this.vragenReeks.findIndex(v => v.id === currentResultaatNextVraagId);
            if (indexVervallenVraag > -1) {
              this.vragenReeks.splice(indexVervallenVraag);
              this.antwoordOpslaan(resultaat);
              this.updateGroepenOfCurrentReeks(this.currentVraag);
            }
          }
        } else {
          // Unable to use opslaanVraag because user must click 'Ga Verder' knop
          // Remove all next questions from current groep
          const vragenOfCurrentGroep: Vraag[] = this.vragenReeks.filter(v => v.groep_id === vraag.groep_id);
          const numberToDelete = vragenOfCurrentGroep.length - (vragenOfCurrentGroep.findIndex(v => v.id === vraag.id)) - 1;
          if (numberToDelete >= 1) {
            const currentIndex: number = this.vragenReeks.findIndex(v => v.id === vraag.id);
            this.vragenReeks.splice(currentIndex + 1, numberToDelete);
            this.antwoordOpslaan(resultaat);
            this.addVraagToReeks(vraag);
            this.setCurrentVraag(vraag);

            if (this.isWijzigen) {
              this.removeUnusedQuestionsFromGroup(vraag);
              this.updateGroepenOfCurrentReeks(vraag);
            }
          } else {
            if (this.isWijzigen) {
              this.antwoordOpslaan(resultaat);
              this.removeUnusedQuestionsFromGroup(vraag);
              this.updateGroepenOfCurrentReeks(vraag);
            }
          }

          // Update session timeout to prevent server side session expiration
          this._refreshSessionService.updateSessionTimeout();
        }
      } else if (this.vraagIdsWithExternalConditions.has(vraag.id)) {
        this.antwoordOpslaan(resultaat);
        this.removeUnusedQuestionsFromGroup(vraag);
        this.updateGroepenOfCurrentReeks(vraag);
      }
      else {
        this.antwoordOpslaan(resultaat);
      }
    }

    if (this.status === SchademeldenState.Verzekeringkiezer) {
      this._dataService.saveResultaten(this.alleResultaten);
    }

    if (VraagHelper.isEindvraag(vraag)
      && (vraag.control === Controltype.MobielSchadeMelden
        || vraag.control === Controltype.Succes
        || vraag.control === Controltype.SuccesVerder
        || vraag.control === Controltype.SuccesFutureState
        || vraag.control === Controltype.SuccesSchadecoach
        || vraag.control === Controltype.SuccesButton)) {
      this.opslaanVraag(vraag, resultaat);
    }
  }

  canSubmitResultaat(resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>): boolean {
    if (resultaat == null) {
      return true;
    }

    if (resultaat.vraag.control === Controltype.Info) {
      return false;
    }

    if ((resultaat.vraag.vraagtype === Vraagtype.OnlineNietMogelijk || resultaat.vraag.vraagtype === Vraagtype.AndersMelden || resultaat.vraag.vraagtype === Vraagtype.Telefoon) && resultaat.vraag.eindvraag) {
      return false;
    }

    if (VraagHelper.isEindvraag(resultaat.vraag) && (!resultaat.vraag.antwoorden || resultaat.vraag.antwoorden.length === 0)) {
      return false;
    }

    return !resultaat.isValid;
  }

  opslaanVraagIfValid(opslaanvraag: boolean, vraag: Vraag, resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>): void {
    if (opslaanvraag) {
      this.opslaanVraag(vraag, resultaat);
    } else {
      this._dataService.setSubmitClicked(true);
    }
  }

  opslaanVraag(vraag: Vraag, resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>) {
    if (vraag.vraagtype === Vraagtype.AndersMelden && vraag.button_link) {
      if (vraag.button_link.startsWith('/')) {
        window.location.href = vraag.button_link;
      } else {
        this._router.navigate(['/externalRedirect', { externalUrl: vraag.button_link }]);
      }
    } else if (resultaat != null && resultaat.isValid) {
      // Update session timeout to prevent server side session expiration
      this._refreshSessionService.updateSessionTimeout();

      if (vraag.vraagtype === Vraagtype.Telefoon && vraag.eindvraag) {
        return;
      } else if (this.volgendeVraagButtonVersturen(null)) {
        this.antwoordOpslaan(resultaat);
        // antwoord volgende vraag opslaan
        let nextVraag: Vraag;
        if (this.currentVraag.linkt_naar?.length > 0) {
          nextVraag = this.vragenboom.vragen.find(v => v.id === this.currentVraag.linkt_naar);
        } else if (this.nextVraagIdBewaren?.length > 0) {
          nextVraag = this.vragenboom.vragen.find(v => v.id === this.nextVraagIdBewaren);
        }
        this.opslaanAntwoordVolgendeVraag(nextVraag, 0);
        this.verstuurVragen();
      } else if (VraagHelper.isEindvraag(vraag) === false) {
        this.opslaanVraagAndShowNext(vraag, resultaat);
      } else {
        this.antwoordOpslaan(resultaat);

        if (vraag.vraagtype === Vraagtype.Versturen) {
          this.verstuurVragen();
        }
      }
    }
  }

  nextVisibleQuestion(resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>) {
    let nextVraagId = '';

    if (this.currentVraag.control === Controltype.Info) {
      nextVraagId = this.currentVraag.linkt_naar;
    } else {
      if (!resultaat) {
        return;
      }
      nextVraagId = resultaat.calculateNextVraagId(this.alleResultaten);
    }

    let nextVraag: Vraag = this.vragenboom.vragen.find(v => v.id === nextVraagId);

    while (nextVraag != null && nextVraag.tonen === false) {
      this.addVraagToReeks(nextVraag);
      this.antwoordOpslaan(HiddenVraagMapper.map(nextVraag));

      let nextVraagId: string = null;
      const standaardAntwoord: Antwoord = nextVraag.antwoorden.find(a => a.standaard_waarde === 'True');
      if (standaardAntwoord) {
        nextVraagId = standaardAntwoord.linkt_naar;
      }

      if (nextVraagId) {
        nextVraag = this.vragenboom.vragen.find(v => v.id === nextVraagId);
      } else {
        return nextVraag;
      }
    }
    return nextVraag;
  }

  private geefSchadedatum(vraagboomAntwoord: VraagboomAntwoord<VraagboomDateResultaat>): string {
    return FormatHelper.formatDate(vraagboomAntwoord.resultaat.value, 'yyyy-MM-dd');
  }

  opslaanVraagAndShowNext(vraag: Vraag, resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>): void {
    if (resultaat == null || !resultaat.isValid) {
      return;
    }

    let nextVraag: Vraag = null;
    let isChangeInReeks = false;

    if (this.isWijzigen !== true && this.isLastInReeks(vraag)) {
      nextVraag = this.nextVisibleQuestion(resultaat);
      isChangeInReeks = true;
    } else {
      // Update state is active --> Determine if vragenreeks will change with new result

      if (!this.isWijzigen) {
        nextVraag = this.nextVisibleQuestion(resultaat);
        isChangeInReeks = true;
      } else {
        nextVraag = this.nextVisibleQuestion(resultaat);
        const newNextQuestionId: string = nextVraag.id;
        const oldResultaat = this.alleResultaten.find(a => a.vraag.id === resultaat.vraag.id);

        if (oldResultaat == null || oldResultaat.calculateNextVraagId(this.alleResultaten) !== newNextQuestionId ||
          this.vragenReeks.findIndex(v => v.id === newNextQuestionId) < 0) {
          // Change in vragenReeks
          const currentIndex = this.vragenReeks.findIndex(v => v.id === vraag.id);
          if (currentIndex !== this.vragenReeks.length - 1) {
            // huidige vraag is niet de laatste in de reeks
            // verwijder vragen na huidige vraag in dezelfde groep uit vragenreeks
            const vragenOfCurrentGroep: Vraag[] = this.vragenReeks.filter(v => v.groep_id === vraag.groep_id);
            const numberToDelete = vragenOfCurrentGroep.length - (vragenOfCurrentGroep.findIndex(v => v.id === vraag.id));
            if (numberToDelete > 1) {
              this.vragenReeks.splice(currentIndex + 1, numberToDelete - 1);
            }
          }

          if (this.vragenReeks.findIndex(v => v.id === newNextQuestionId) >= 0) {
            nextVraag = this.vragenReeks[this.vragenReeks.length - 1];
          } else {
            nextVraag = this.nextVisibleQuestion(resultaat);
            isChangeInReeks = true;
          }
        }
        else {
          const vragenOfCurrentGroep: Vraag[] = this.visibleVragenReeks.filter(v => v.groep_id === vraag.groep_id);
          const index = vragenOfCurrentGroep.findIndex(v => v.id === vraag.id);
          if (index >= vragenOfCurrentGroep.length - 1) {
            // Vraag is last of group
            const laatsteVraagInReeks = this.vragenReeks[this.vragenReeks.length - 1];
            const originalLaatsteVraagInReeks = this.originalVragenReeks[this.originalVragenReeks.length - 1];

            if (laatsteVraagInReeks.id === originalLaatsteVraagInReeks.id) {
              // No change in last question -> show last question
              nextVraag = this.vragenReeks[this.vragenReeks.length - 1];
            } else {
              // Last question changed -> show next question
              const newVraagIndex = this.vragenReeks.findIndex(v => v.id === newNextQuestionId);
              if (newVraagIndex >= 0) {
                nextVraag = this.vragenReeks[newVraagIndex];
              } else {
                nextVraag = this.nextVisibleQuestion(resultaat);
                isChangeInReeks = true;
              }
            }
          } else {
            // Vraag is not last of group --> show next question of group
            nextVraag = vragenOfCurrentGroep[index + 1];
          }
        }
      }
    }

    // antwoord opslaan
    this.antwoordOpslaan(resultaat);

    // bij opslaan schadedatum, emit claimvoorbereiden
    if (this.proces !== Proces.SchademeldenZakelijk && this._dataService.getProces() !== Proces.SchademeldenZakelijkAnoniem
      && vraag.control === Controltype.Kalender && resultaat != null && vraag.antwoorden[0].variabeleWaarde === '{Schadedatum}') {
      this.prepareClaimVoorbereiden.emit(new ClaimVoorbereidenEventargs(this._verzekering.polisnummer,
        this.geefSchadedatum(resultaat as VraagboomAntwoord<VraagboomDateResultaat>)));
    }

    if (nextVraag && nextVraag != null) {
      if ((this.currentVraag && nextVraag.groep_id !== this.currentVraag.groep_id) || isChangeInReeks) {
        this.removeUnusedQuestionsFromGroup(nextVraag);
      }
      if (isChangeInReeks) {
        this.addVraagToReeks(nextVraag);
      }
      this.setCurrentVraag(nextVraag);

      if (this._dataService.getProces() === Proces.SchadeCoach) {
        if (nextVraag.control === Controltype.SuccesSchadecoach) {
          this.bepaalIdsSchadecoach();
          window.dispatchEvent(new Event("schadecoach:klaar"));
          this.schadecoachKlaar = true;
        } else if (this.schadecoachKlaar) {
          window.dispatchEvent(new Event("schadecoach:reset"));
          this.schadecoachKlaar = false;
        }
      }
    }
  }

  verstuurVragen(): any {
    if (this.vragenReeks == null || this.alleResultaten == null) {
      this.submitVraagboom.emit([]);
    }
    this.submitVraagboom.emit(this.combineVragenReeksAndResultaten(this.vragenReeks, this.alleResultaten));
  }

  combineVragenReeksAndResultaten(vragenReeks: Vraag[], resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]):
    VraagboomAntwoord<IBaseVraagboomResultaat>[] {
    if (vragenReeks == null || resultaten == null) {
      return [];
    }
    // Controleren of antwoorden van de eindvraag (bij schadedetails) toegevoegd moeten worden.
    const eindVraag: Vraag = this.vragenboom.vragen.find(v => v.eindvraag);
    if (eindVraag.vraagtype === Vraagtype.Versturen && !eindVraag.tonen && (eindVraag.control === Controltype.Summary || eindVraag.control === Controltype.Button)) {
      if (eindVraag.control === Controltype.Button && this.vragenReeks.findIndex(v => v.id === eindVraag.id) === -1) {
        this.vragenReeks.push(eindVraag);
      }
      if (this.alleResultaten.findIndex(a => a.vraag.id === eindVraag.id) === -1) {
        this.antwoordOpslaan(HiddenVraagMapper.map(eindVraag));
      }
    }

    const finalResultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[] = [];
    let vraag: Vraag = vragenReeks.find(v => v.startvraag);
    while (vraag != null) {
      const resultaat = resultaten.find(r => r.vraag.id === vraag.id);
      if (resultaat == null) {
        return [];
      }
      finalResultaten.push(cloneDeep(resultaat));
      vraag = vragenReeks.find(v => v.id === resultaat.calculateNextVraagId(this.alleResultaten));
    }

    return finalResultaten;
  }

  isLastInReeks(vraag: Vraag): boolean {
    if (vraag == null || this.vragenReeks == null || this.vragenReeks.length === 0) {
      return false;
    }

    return this.vragenReeks[this.vragenReeks.length - 1].id === vraag.id;
  }

  showGroepVraag(resultaat: VraagboomAntwoord<IBaseVraagboomResultaat>): boolean {
    if (resultaat.vraag.id === this.currentVraag.id) {
      return true;
    }
    return this.status === SchademeldenState.Verzekeringkiezer && !resultaat.antwoordVerbergen;
  }

  setCurrentVraag(vraag: Vraag) {
    if (!vraag || this.currentVraag === vraag) {
      return;
    }

    this.currentVraag = vraag;
    this.currentVraagChange.emit(new CurrentvraagEventargs(vraag, this.vragenReeks));
    this._dataService.setSubmitClicked(false);

    if (this.currentVraag.eindvraag && this.currentVraag.vraagtype === Vraagtype.Versturen
      && this.currentVraag.control === Controltype.Summary && !this.currentVraag.tonen) {
      this.verstuurVragen();
    } else {
      if (this.status === SchademeldenState.Verzekeringkiezer && this.currentVraag.antwoorden) {
        if (this.currentVraag.antwoorden?.length === 1) {
          // Als bij de verzekeringskiezer maar 1 antwoord is, dan deze selecteren
          const antwoordResultaat = new VraagboomAntwoord<VraagboomEmptyResultaat>();
          antwoordResultaat.vraag = this.currentVraag;
          antwoordResultaat.antwoorden = this.currentVraag.antwoorden;
          antwoordResultaat.resultaat = new VraagboomEmptyResultaat();
          antwoordResultaat.isValid = true;
          antwoordResultaat.antwoordVerbergen = true;
          this.opslaanVraagAndShowNext(this.currentVraag, antwoordResultaat);
        }
        if (this.currentVraag.antwoorden?.length > 1) {
          // Als bij de verzekeringskiezer meer dan 1 antwoord is, maar er wel een antwoord gekozen is dan antwoord leeg maken
          const index = this.alleResultaten.findIndex(a => a.vraag.id === this.currentVraag.id);
          if (index > -1) {
            this.alleResultaten = this.alleResultaten.filter(r => r.vraag.id !== this.currentVraag.id);
          }
          const currentgroep: Groep = this.vraagGroepen.find(g => g.id === this.currentVraag.groep_id);
          const resultaatCurrentVraag = currentgroep.resultaten.find(r => r.vraag.id === this.currentVraag.id);
          if (resultaatCurrentVraag.antwoorden) {
            resultaatCurrentVraag.antwoorden = [];
            resultaatCurrentVraag.resultaat = new VraagboomAntwoord<VraagboomEmptyResultaat>();
          }
        }
      }

      setTimeout(() => {
        if (this.currentVraagElement) {
          if (this.status === SchademeldenState.Verzekeringkiezer) {
            // scroll naar volgende vraag
            this.scrollElementIntoView(this.currentVraagElement.nativeElement);
          } else {
            if (this.proces !== Proces.Schadedetail) {
              // scroll naar boven
              const scrollToTop = window.setInterval(() => {
                const pos: number = window.pageYOffset;
                if (pos > 0) {
                  window.scrollTo(0, pos - 20);
                } else {
                  window.clearInterval(scrollToTop);
                }
              }, 16);
            }
          }
        }
      });
      this.bewaarMeetgegevens();
    }
  }

  scrollElementIntoView(el: HTMLElement) {
    if (el == null)
      return;

    const rect = el.getBoundingClientRect();
    const elementIsInView =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom + 100 <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);

    if (elementIsInView)
      return;

    let scrollAction = () => el.scrollIntoView({ behavior: 'smooth' });

    const appRootItems = document.getElementsByTagName("app-root");
    if (appRootItems?.length == 1) {
      const appRootElement = appRootItems.item(0) as HTMLElement;
      if (appRootElement != null) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - appRootElement.offsetTop;
        scrollAction = () => window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }

    scrollAction();
  }

  vragenreeksIsFinal(): boolean {
    if (!this.currentVraag) {
      return true;
    }

    return VraagHelper.isEindvraagInProces(this.currentVraag);
  }

  isEersteVraagKiezer(vraag: Vraag): boolean {
    if (vraag == null) {
      return false;
    }
    return (this.status === SchademeldenState.Verzekeringkiezer && vraag.startvraag);
  }

  gaNaarStart() {
    this._router.navigate(['/intro']);
  }

  hasPreviousQuestion(vraag: Vraag): boolean {
    if (vraag == null || this.vragenReeks == null || this.hasAnnuleren(vraag)) {
      return false;
    }
    if ((this.status === SchademeldenState.Schademelden && this.proces !== Proces.Schadedetail) || this.status === SchademeldenState.Samenvatting) {
      return true;
    }
    return this.visibleVragenReeks.findIndex(v => v.id === vraag.id) > 0;
  }

  clickVorige(groep: Groep) {
    if (this.hasAnnuleren(this.currentVraag)) {
      this.annulerenWijzigen();
    } else if (this.hasPreviousQuestion(this.currentVraag)) {
      this.vorigeVraag(this.currentVraag, this.getResultaatCurrentGroep(groep, this.currentVraag));
    } else if (this.isEersteVraagKiezer(this.currentVraag)) {
      this.gaNaarStart();
    }
  }

  geefTekstButtonVersturen(): string {
    const nextVraag = this.vragenboom.vragen.find(v => v.id === this.currentVraag.linkt_naar);
    if (nextVraag && nextVraag != null && nextVraag.antwoorden?.length > 1) {
      return nextVraag.antwoorden[1].titel;
    }
    return '';
  }

  opslaanVraagEnVersturen() {
    // van de huidige en volgende vraag het antwoord opslaan
    let resultaat = new VraagboomAntwoord<VraagboomEmptyResultaat>();
    resultaat.vraag = this.currentVraag;
    resultaat.antwoorden = [].concat(this.currentVraag.antwoorden);
    resultaat.resultaat = new VraagboomEmptyResultaat();
    resultaat.isValid = true;
    this.antwoordOpslaan(resultaat);
    this.opslaanAntwoordVolgendeVraag(this.vragenboom.vragen.find(v => v.id === this.currentVraag.linkt_naar), 1);
    this.verstuurVragen();
  }

  opslaanAntwoordVolgendeVraag(nextVraag: Vraag, index: number): void {
    if (nextVraag && nextVraag != null && nextVraag.antwoorden?.length > index) {
      this.vragenReeks.push(nextVraag);
      let resultaat = new VraagboomAntwoord<VraagboomEmptyResultaat>();
      resultaat.vraag = nextVraag;
      resultaat.antwoorden = [].concat(nextVraag.antwoorden[index]);
      resultaat.resultaat = new VraagboomEmptyResultaat();
      resultaat.isValid = true;
      this.antwoordOpslaan(resultaat);
    }
  }

  volgendeVraagExtraButtonVersturen(): boolean {
    const nextVraag = this.vragenboom.vragen.find(v => v.id === this.currentVraag.linkt_naar);
    return this.volgendeVraagButtonVersturen(null) && nextVraag?.antwoorden?.length > 1;
  }

  volgendeVraagButtonVersturen(groep: Groep): boolean {
    let nextVraag: Vraag = null;
    if (this.currentVraag.linkt_naar?.length > 0) {
      nextVraag = this.vragenboom.vragen.find(v => v.id === this.currentVraag.linkt_naar);
    } else {
      if (groep != null) {
        nextVraag = this.nextVisibleQuestion(this.getResultaatCurrentGroep(groep, this.currentVraag));
      }
      if (nextVraag == null && this.nextVraagIdBewaren?.length > 0) {
        nextVraag = this.vragenboom.vragen.find(v => v.id === this.nextVraagIdBewaren);
      }
    }
    return (nextVraag?.control === Controltype.Button && nextVraag?.vraagtype === Vraagtype.Versturen);
  }

  geefTekstVorigeKnop(): string {
    if (this.hasAnnuleren(this.currentVraag)) {
      return 'Annuleren';
    }
    if (this.hasPreviousQuestion(this.currentVraag) || this.isEersteVraagKiezer(this.currentVraag)) {
      return 'Terug';
    }
    return '';
  }

  geefTekstVolgendeKnop(groep: Groep, vraag: Vraag): string {
    if (this.isLegeUploadvraag(groep, vraag)) {
      return 'Overslaan';
    }
    if (this.isVolgendeVraagHiddenVersturen(vraag)) {
      if (vraag.vraagtype === Vraagtype.Foto) {
        return 'Foto\'s versturen';
      }
      return 'Verstuur documenten';
    }

    if ((vraag.vraagtype === Vraagtype.Telefoon || vraag.vraagtype === Vraagtype.OnlineNietMogelijk) && vraag.eindvraag) {
      return 'Bel ons';
    }
    if (vraag.eindvraag || this.volgendeVraagButtonVersturen(groep)) {
      // Bij contactgegevens staat er "Telefoonnummer" in antwoordtitel
      if (vraag.eindvraag && vraag.control != Controltype.ContactGegevens && vraag.antwoorden?.length > 0 && vraag.antwoorden[0].titel) {
        return vraag.antwoorden[0].titel;
      }
      return 'Verzenden';
    }
    if (vraag.button_text && vraag.button_text != '') {
      return vraag.button_text;
    }

    return 'Ga verder';
  }

  hasAnnuleren(vraag: Vraag): boolean {
    return this.isWijzigen && vraag.id === this.originalFirstQuestionId;
  }

  isEindeProces(vraag: Vraag): boolean {
    return VraagHelper.isEindvraagInProces(vraag);
  }

  isLegeUploadvraag(groep: Groep, vraag: Vraag): boolean {
    if (!vraag.verplicht && (vraag.vraagtype === Vraagtype.Foto || vraag.vraagtype === Vraagtype.Upload)) {
      const resultaat = this.getResultaatCurrentGroep(groep, vraag) as VraagboomAntwoord<VraagboomUploadResultaat>;
      return !resultaat || !resultaat.resultaat || resultaat.resultaat.uploadedFiles.length <= 0;
    }
    return false;
  }

  isVolgendeVraagHiddenVersturen(vraag: Vraag): boolean {
    if (vraag.linkt_naar && vraag.linkt_naar !== null) {
      const nextVraag = this.vragenboom.vragen.find(v => v.id === vraag.linkt_naar);
      if (nextVraag && nextVraag != null) {
        return (!nextVraag.tonen && nextVraag.eindvraag
          && nextVraag.vraagtype === Vraagtype.Versturen
          && nextVraag.control !== Controltype.Button);
      }
    }
    return false;
  }

  updateGroepenOfCurrentReeks(currvraag: Vraag): void {
    if (this.vragenReeks == null) {
      this.vraagGroepen = [];
      return;
    }

    const groepen: Groep[] = this.calculateAlleGroepen(currvraag);
    for (const vraag of this.visibleVragenReeks) {
      const groep: Groep = groepen.find(g => g.id === vraag.groep_id);
      if (groep != null) {
        let resultaat: VraagboomAntwoord<IBaseVraagboomResultaat> = this.alleResultaten.find(r => r.vraag.id === vraag.id);
        if (resultaat == null) {
          if (VraagHelper.isComplexType(vraag)) {
            const corresResultaat = this.alleResultaten.find(r => r.vraag.groep_id === vraag.groep_id && r.vraag.control === vraag.control);
            if (corresResultaat && !groep.resultaten.find(r => r.vraag.id === corresResultaat.vraag.id)) {
              resultaat = ResultaatMapper.map(corresResultaat, vraag);
              if (resultaat) {
                this.alleResultaten = this.alleResultaten.filter(r => r.vraag.id !== corresResultaat.vraag.id);
                this.antwoordOpslaan(resultaat);
              }
            }
          }

          if (resultaat) {
            groep.resultaten.push(resultaat);
          } else {
            resultaat = new VraagboomAntwoord();
            resultaat.vraag = vraag;
            groep.resultaten.push(resultaat);
          }
        } else {
          groep.resultaten.push(cloneDeep(resultaat));
        }
      } else {
        const newGroep: Groep = new Groep(vraag.groep_id, '');
        const resultaat = new VraagboomAntwoord();
        resultaat.vraag = vraag;
        newGroep.resultaten.push(resultaat);
        groepen.push(newGroep);
      }
    }

    this.vraagGroepen = groepen;
  }

  calculateAlleGroepen(currvraag: Vraag): Groep[] {
    if (this.vragenboom == null || this.vragenboom.vragen == null) {
      return [];
    }
    let groepen: Groep[] = [];

    for (let i = 0; i < this.vragenReeks.length; i++) {
      const vraag = this.vragenReeks[i];
      let titel = vraag.groep_titel;
      const groepId = vraag.groep_id;

      // don't add the groep van de summary
      if (vraag.control === Controltype.Summary) {
        continue;
      }
      // don't add duplicate groepen
      if (groepen.findIndex(g => g.id === groepId) >= 0) {
        continue;
      }
      // dont't add groep without visible question
      if (!vraag.tonen) {
        continue;
      }
      // replace groep titel when it only contains one question
      const VragenInGroep = this.vragenReeks.filter(g => g.id === groepId);
      if (VragenInGroep.length === 1) {
        titel = VragenInGroep[0].titel;
      }

      groepen.push(new Groep(groepId, titel));
    }

    return groepen;
  }

  summaryGroep(groep: Groep): string {
    if (groep == null || groep.resultaten == null) {
      return '';
    }

    if (groep.resultaten.findIndex(r => r.vraag.control === Controltype.Summary) >= 0) {
      return 'summary-group';
    }
    return '';
  }

  getResultaatCurrentGroep(groep: Groep, vraag: Vraag): VraagboomAntwoord<IBaseVraagboomResultaat> {
    if (groep == null || groep.resultaten == null || vraag == null) {
      return null;
    }

    return groep.resultaten.find(r => r.vraag.id === vraag.id);
  }

  filterInfo(resultaten): VraagboomAntwoord<IBaseVraagboomResultaat>[] {
    let response: VraagboomAntwoord<IBaseVraagboomResultaat>[] = [];

    resultaten.forEach(r => {
      if (r.vraag.control !== Controltype.Info) {
        response.push(r);
      }
    });

    return response;
  }

  bepaalIdsSchadecoach(): void {
    this.idSchadecoach = '';
    this.idsSchadecoach = '';
    let result = new VraagboomAntwoord<VraagboomEmptyResultaat>();

    if (this.vragenReeks?.length > 0) {
      this.vragenReeks.forEach((vraag: Vraag) => {
        result = this.alleResultaten.find(a => a.vraag.id === vraag.id);
        if (result?.antwoorden?.length > 0) {
          this.idsSchadecoach += vraag.id + '=' + result.antwoorden[0].id + '&';
          if (vraag.aliassen?.length > 0) {
            vraag.aliassen.forEach(alias => {
              this.idsSchadecoach += alias + '=' + result.antwoorden[0].id + '&';
            });
          }
          this.idSchadecoach = vraag.id + '=' + result.antwoorden[0].id;
        }
      });
    }
  }

  bewaarMeetgegevens() {
    let sleutelMeten = '';

    if (this.status === SchademeldenState.Afhandeling || this.status === SchademeldenState.Afronden) {
      sleutelMeten = this.vragenboom.vragenboomMetadata.sleutel.toLowerCase() + '-';
    } else {
      if (!this.verzekeringOpgehaald) {
        this._verzekering = this._dataService.getVerzekering();
        this.verzekeringOpgehaald = true;
      }
      if (this._verzekering != null && this._verzekering.sleutel != null && this._verzekering.sleutel) {
        sleutelMeten = MetenMapper.format(this._verzekering.sleutel) + '-';
      }
    }

    this._dataService.saveMeetGegevens(this.status, sleutelMeten, this.vragenReeks);
  }

  volgnummer(vraag: Vraag): string {
    return MetenMapper.geefVolgnr(vraag, this.vragenReeks) + '_';
  }

  plusSleutel(): string {
    let response = '';
    if (this.status === SchademeldenState.Verzekeringkiezer) {
      response = 'kiezer-';
    } else {
      if (this.status === SchademeldenState.Afronden) {
        let sleutel = this._meetgegevens.sleutel.toLowerCase();
        if (this.meetKey?.length > 0) {
          sleutel = sleutel.replace('bijwerkenclaim', 'klanttaak');
        }
        return 'afronden-' + this._dataService.getProductOrOmschrijvingLineOfBusiness().toLowerCase() + '-' + sleutel;
      } else {
        response = 'indienen-';
      }
    }

    if (this._verzekering != null && this._verzekering.sleutel != null && this._verzekering.sleutel) {
      if (this.status === SchademeldenState.Afhandeling) {
        return response + MetenMapper.format(this._verzekering.sleutel) + '-' + this.vragenboom.vragenboomMetadata.sleutel.toLowerCase() + '-';
      }
      return response + MetenMapper.format(this._verzekering.sleutel) + '-';
    }
    return response;
  }

  geefVolgnr(groep: Groep, reeks: Vraag[]): string {
    const groepIdsOfSummary: string[] = [];

    reeks.forEach((vr: Vraag) => {
      if (groepIdsOfSummary.indexOf(vr.groep_id) < 0) {
        groepIdsOfSummary.push(vr.groep_id);
      }
    });

    const volgnummer: number = groepIdsOfSummary.indexOf(groep.id) + 1;
    return volgnummer.toString();
  }

  geefMeetidWijzigen(groep: Groep): string {
    if (this.status === SchademeldenState.Samenvatting) {
      return 'ipoclick-lnk-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel()
        + this.volgnummer(this.currentVraag) + MetenMapper.format(this.currentVraag.titel)
        + '-wijzigen-' + this.geefVolgnr(groep, this.vragenReeks);
    } else {
      return 'ipoclick-lnk-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel()
        + this.volgnummer(groep.resultaten[0].vraag) + MetenMapper.format(groep.titel) + '-wijzigen';
    }
  }

  geefMeetidVorige(): string {
    if (this.hasAnnuleren(this.currentVraag)) {
      return 'ipoclick-lnk-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel() +
        this.volgnummer(this.currentVraag) + MetenMapper.format(this.currentVraag.titel) + '-annuleren';
    }
    return 'ipoclick-lnk-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel() +
      this.volgnummer(this.currentVraag) + MetenMapper.format(this.currentVraag.titel) + '-ga_terug';
  }

  geefMeetidVolgende(groep: Groep, vraag: Vraag): string {
    return 'ipoclick-btn' + (this.canSubmitResultaat(this.getResultaatCurrentGroep(groep, vraag)) ? '_inactief' : '')
      + '-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel() + this.volgnummer(vraag)
      + MetenMapper.format(vraag.titel) + '-' + MetenMapper.format(this.geefTekstVolgendeKnop(groep, vraag));
  }

  geefMeetidButtonVersturen(vraag: Vraag): string {
    return 'ipoclick-btn' + '-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel() + this.volgnummer(vraag)
      + MetenMapper.format(vraag.titel) + '-' + MetenMapper.format(this.geefTekstButtonVersturen());
  }

  geefIpoView(): string {
    return 'ipoview-' + this._dataService.getMeetidPazaProces() + '-' + this.plusSleutel()
      + this.volgnummer(this.currentVraag) + MetenMapper.formatTitelMetToevoeging(this.currentVraag.titel, this.currentVraag.titel_toevoeging);
  }
}
